/**
 * One station on the route. Five appearances across three families, and never
 * an intermediate one: quest stations are todo, hot or done; reward markers
 * are locked or earned; a gate is neither (contract 0.3.0).
 */

import { BoltIcon, LockIcon, TickIcon } from "./icons";
import { packAsset } from "./assets";
import type { Station } from "./layout";
import type { WidgetTemplate } from "../data/contract";

interface Props {
  station: Station;
  template: WidgetTemplate;
  /** The words a card shows, written on this side from the platform's facts. */
  title: string;
  /** Entrance delay, so the bottom of the route pops first. */
  pop: string;
  /** Bobbing must never synchronise; one shared metronome is the tell. */
  bob: { animationDuration: string; animationDelay: string };
  showCoins: boolean;
  onOpen(station: Station): void;
}

export function StationNode({ station, template, title, pop, bob, showCoins, onOpen }: Props) {
  const position = { left: `${station.x}px`, top: `${station.y}px`, "--pop": pop } as React.CSSProperties;

  const interactive = {
    role: "button",
    tabIndex: 0,
    onClick: () => onOpen(station),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(station);
      }
    },
  };

  if (station.kind === "quest" && station.quest) {
    const { state, campaign, fraction } = station.quest;

    if (state === "done") {
      const paid =
        showCoins && campaign.reward?.reward.kind === "credits"
          ? `${campaign.reward.reward.value} ${campaign.reward.reward.currency ?? "coins"}`
          : null;
      return (
        <div className="node done" style={position} aria-label={`${title}, completed`} {...interactive}>
          <div className="node-disc"><TickIcon /></div>
          {paid && (
            <div className="memory">
              <img src={packAsset(template, "coin")} alt="" />
              {paid}
            </div>
          )}
        </div>
      );
    }

    if (state === "hot") {
      const target = campaign.goal.target || 1;
      return (
        <div className="node hot" style={position} aria-label={`${title}, current quest`} {...interactive}>
          <div className="ring" />
          <div className="node-disc"><BoltIcon /></div>
          <div className="start-bubble" style={bob}>YOU ARE HERE</div>
          <div className="quest-pill">
            {title} &middot;{" "}
            <b className="tnum">
              {Math.round(fraction * target)} of {target}
            </b>
          </div>
        </div>
      );
    }

    return (
      <div className="node todo" style={position} aria-label={title} {...interactive}>
        <div className="node-disc" />
      </div>
    );
  }

  if (station.kind === "reward" && station.marker) {
    const { state, label, assetKey, requirement } = station.marker;
    // No container and nothing to open. The reward is the node, dim until it
    // is yours: concealment is chance coded, and this app conceals nothing.
    return (
      <div
        className={`node reward ${state}`}
        style={position}
        aria-label={`${label}, ${state === "earned" ? "earned" : "locked"}`}
        {...interactive}
      >
        <img src={packAsset(template, assetKey)} alt="" style={state === "earned" ? bob : undefined} />
        <div className="rw-cap">
          <div className="rw-label">{label}</div>
          {requirement && <span className="rw-need">{requirement}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="node gate" style={position} aria-label={station.gate?.label} {...interactive}>
      <div className="gate-pill">
        <LockIcon />
        {station.gate?.label}
      </div>
    </div>
  );
}
